import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import AppShell from "./AppShell";
import axios from "axios";
import { RefreshCw, Play, Pause, Trash2, Calendar, Clock, ChevronRight } from "lucide-react";

const INTERVAL_LABELS = { weekly: "Weekly", biweekly: "Bi-weekly", monthly: "Monthly", quarterly: "Quarterly", yearly: "Yearly" };

const RecurringInvoices = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [recurring, setRecurring] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSetup, setShowSetup] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [setupForm, setSetupForm] = useState({ invoice_id: "", interval: "monthly", auto_send: true });

  useEffect(() => { fetchRecurring(); fetchInvoices(); }, []);

  const fetchRecurring = async () => {
    try { const { data } = await axios.get(`${API}/recurring`); setRecurring(data); } catch (e) {} finally { setLoading(false); }
  };
  const fetchInvoices = async () => {
    try { const { data } = await axios.get(`${API}/invoices`); setInvoices(data.filter(inv => !inv.is_recurring)); } catch (e) {}
  };

  const handleCreate = async () => {
    if (!setupForm.invoice_id) { alert("Select an invoice to repeat"); return; }
    try {
      await axios.post(`${API}/invoices/${setupForm.invoice_id}/set-recurring`, setupForm);
      setShowSetup(false);
      setSetupForm({ invoice_id: "", interval: "monthly", auto_send: true });
      fetchRecurring();
    } catch (err) { alert(err.response?.data?.detail || "Failed to set up recurring"); }
  };

  const handleToggle = async (id, active) => {
    try { await axios.put(`${API}/recurring/${id}`, { active: !active }); fetchRecurring(); } catch (e) { alert("Failed"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Cancel this recurring invoice?")) return;
    try { await axios.delete(`${API}/recurring/${id}`); fetchRecurring(); } catch (e) { alert("Failed"); }
  };

  const formatCurrency = (amount, currency = "USD") => new Intl.NumberFormat("en-US", { style: "currency", currency }).format(amount || 0);

  return (
    <AppShell active="recurring">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-[22px] sm:text-[28px] tracking-[-0.03em] text-[#0A0A0A]">Recurring Invoices</h1>
          <p className="text-[13px] sm:text-[14px] text-[#6B7280] mt-1">Auto-create and send invoices on a schedule</p>
        </div>
        <button onClick={() => setShowSetup(!showSetup)} className="btn-primary text-[13px] px-4 py-2.5 flex items-center gap-2 w-fit" data-testid="new-recurring-btn">
          <RefreshCw className="w-4 h-4" /> Set up recurring
        </button>
      </div>

      {/* Setup Form */}
      {showSetup && (
        <div className="bg-white border border-[#E5E7EB] p-5 mb-6 space-y-4" data-testid="recurring-setup-form">
          <h2 className="font-semibold text-[16px] text-[#0A0A0A]">Create Recurring Invoice</h2>
          <p className="text-[13px] text-[#6B7280]">Select an existing invoice as a template. A new invoice will be created and optionally sent on the selected schedule.</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[12px] font-semibold text-[#6B7280] uppercase mb-1.5">Source invoice</label>
              <select value={setupForm.invoice_id} onChange={e => setSetupForm(p => ({ ...p, invoice_id: e.target.value }))} className="input text-[14px]" data-testid="recurring-source">
                <option value="">Select an invoice...</option>
                {invoices.map(inv => (
                  <option key={inv.id} value={inv.id}>{inv.invoice_number} — {inv.client?.name} ({formatCurrency(inv.total, inv.currency)})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[12px] font-semibold text-[#6B7280] uppercase mb-1.5">Frequency</label>
              <select value={setupForm.interval} onChange={e => setSetupForm(p => ({ ...p, interval: e.target.value }))} className="input text-[14px]" data-testid="recurring-interval">
                <option value="weekly">Weekly</option>
                <option value="biweekly">Bi-weekly</option>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="yearly">Yearly</option>
              </select>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-[14px] text-[#0A0A0A] cursor-pointer">
              <input type="checkbox" checked={setupForm.auto_send} onChange={e => setSetupForm(p => ({ ...p, auto_send: e.target.checked }))} className="w-4 h-4" data-testid="recurring-autosend" />
              Auto-send to client when created
            </label>
          </div>
          <div className="flex gap-2">
            <button onClick={handleCreate} className="btn-primary text-[13px] px-4 py-2 flex items-center gap-1.5" data-testid="create-recurring-btn"><RefreshCw className="w-4 h-4" /> Create</button>
            <button onClick={() => setShowSetup(false)} className="btn-outline text-[13px] px-4 py-2">Cancel</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="py-12 text-center"><p className="text-[14px] text-[#6B7280]">Loading...</p></div>
      ) : recurring.length === 0 ? (
        <div className="py-16 text-center bg-white border border-[#E5E7EB]">
          <RefreshCw className="w-8 h-8 text-[#D1D5DB] mx-auto mb-3" />
          <p className="text-[15px] text-[#6B7280] mb-1">No recurring invoices yet</p>
          <p className="text-[13px] text-[#9CA3AF] mb-4">Set up a recurring invoice to auto-create and send invoices on a schedule.</p>
          <button onClick={() => setShowSetup(true)} className="btn-primary text-[13px] px-4 py-2 inline-flex items-center gap-2" data-testid="empty-recurring-btn">
            <RefreshCw className="w-4 h-4" /> Set up recurring
          </button>
        </div>
      ) : (
        <div className="space-y-3" data-testid="recurring-list">
          {recurring.map(rec => (
            <div key={rec.id} className="bg-white border border-[#E5E7EB] p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid={`recurring-${rec.id}`}>
              <div className="flex items-start gap-4">
                <div className={`w-10 h-10 flex items-center justify-center flex-shrink-0 ${rec.active ? "bg-[#ECFDF5]" : "bg-[#F3F4F6]"}`}>
                  <RefreshCw className={`w-5 h-5 ${rec.active ? "text-[#059669]" : "text-[#9CA3AF]"}`} />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-[15px] text-[#0A0A0A]">{rec.client?.name || "Unknown client"}</span>
                    <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 ${rec.active ? "text-[#059669] bg-[#ECFDF5]" : "text-[#9CA3AF] bg-[#F3F4F6]"}`}>
                      {rec.active ? "Active" : "Paused"}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px] text-[#6B7280]">
                    <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {INTERVAL_LABELS[rec.interval] || rec.interval}</span>
                    <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> Next: {rec.next_send_date}</span>
                    <span className="font-mono font-semibold text-[#0A0A0A]">{formatCurrency(rec.items?.reduce((s, i) => s + (i.amount || 0), 0), rec.currency)}</span>
                    <span>{rec.invoices_created} sent</span>
                  </div>
                  {rec.auto_send && <span className="text-[11px] text-[#059669] font-medium mt-1 inline-block">Auto-sends to client</span>}
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button onClick={() => handleToggle(rec.id, rec.active)} className={`p-2 hover:bg-[#F3F4F6] ${rec.active ? "text-[#D97706]" : "text-[#059669]"}`} title={rec.active ? "Pause" : "Resume"} data-testid={`toggle-${rec.id}`}>
                  {rec.active ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                </button>
                <button onClick={() => handleDelete(rec.id)} className="p-2 hover:bg-[#FEF2F2] text-[#9CA3AF] hover:text-[#EF4444]" title="Cancel" data-testid={`delete-recurring-${rec.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </AppShell>
  );
};

export default RecurringInvoices;
